// Cartography (UI-8 / D15, M18) — the pure filled-map engine. A crafted map
// captures a *world-locked* 128×128 snapshot: unlike the live minimap radar
// (which recenters on the player), a map is anchored to a fixed grid cell chosen
// at creation and fills in as the player explores within its bounds.
//
// This module is pure (ENG-1): no three/DOM/world imports. It samples the world
// through a narrow `MapSampler` interface and colours each pixel from a per-state
// palette, applying Minecraft's north-facing relief shading (brighter uphill,
// darker downhill). The host (boot) owns a `MapStore`, wires the sampler to the
// real World, and the HUD blits `MapSnapshot.rgba`.
import { CARTO } from '../config';
import type { Registries } from '../registry/load';

export const MAP_PX = CARTO.MAP_PX;

/** Blocks covered by one map pixel at `scale` (1,2,4,8,16 for scale 0..4). */
export function blocksPerPixel(scale: number): number {
  return 1 << scale;
}

/** Blocks across a whole map at `scale` (128,256,512,1024,2048). */
export function mapSpan(scale: number): number {
  return MAP_PX << scale;
}

/** World-block centre a fresh map snaps to (MC `MapItem.createNewSavedData`):
 *  maps tile the world in `mapSpan`-block cells, so the creation point lands near
 *  the central pixel and re-creating nearby reuses the same framing. Deterministic. */
export function mapCenter(scale: number, x: number, z: number): { cx: number; cz: number } {
  const i = mapSpan(scale);
  const j = Math.floor((x + 64) / i);
  const k = Math.floor((z + 64) / i);
  return { cx: j * i + (i >> 1) - 64, cz: k * i + (i >> 1) - 64 };
}

/** The world surface a `MapSnapshot` reads: top block + its feet-Y (for relief). */
export interface MapSampler {
  /** Top (surface) block state id at column (wx,wz); state 0 (air) if unloaded. */
  columnTopState(wx: number, wz: number): number;
  /** Surface feet-Y at a column, or null if the chunk isn't loaded. */
  surfaceY(wx: number, wz: number): number | null;
}

/** Per-state base map colour (UI-8): `null` for air/unmapped, else an RGB triple. */
export type MapPalette = ReadonlyArray<readonly [number, number, number] | null>;

/** One map cell after relief shading — the base colour, its shade tier, and the
 *  shaded RGB actually painted (null when the column is unmapped/air). */
export interface MapCell {
  base: readonly [number, number, number] | null;
  shade: 0 | 1 | 2;
  rgb: [number, number, number] | null;
}

/** Relief shade tier from this column's height vs the pixel one step north.
 *  Mirrors MC's dithered slope test: uphill→bright(2), flat→normal(1), down→dark(0). */
export function reliefShade(scale: number, u: number, v: number, here: number, north: number): 0 | 1 | 2 {
  const relief = ((here - north) * 4) / (blocksPerPixel(scale) + 4) + (((u + v) & 1) - 0.5) * 0.4;
  return relief > CARTO.RELIEF_HI ? 2 : relief < CARTO.RELIEF_LO ? 0 : 1;
}

/** Colour + shade for pixel (u,v) of a scale/centre map, sampling the world. */
export function computeCell(sampler: MapSampler, palette: MapPalette, scale: number, cx: number, cz: number, u: number, v: number): MapCell {
  const bpp = blocksPerPixel(scale);
  const originX = cx - mapSpan(scale) / 2;
  const originZ = cz - mapSpan(scale) / 2;
  const wx = originX + u * bpp;
  const wz = originZ + v * bpp;
  const base = palette[sampler.columnTopState(wx, wz)] ?? null;
  if (!base) return { base: null, shade: 1, rgb: null };
  const here = sampler.surfaceY(wx, wz);
  const north = sampler.surfaceY(wx, wz - bpp);
  const shade = here !== null && north !== null ? reliefShade(scale, u, v, here, north) : 1;
  const m = CARTO.SHADE_MUL[shade];
  return { base, shade, rgb: [Math.round(base[0] * m), Math.round(base[1] * m), Math.round(base[2] * m)] };
}

/** A player/target pin on the map face: pixel coords (may sit outside 0..128 when
 *  the point is off-map — the HUD clamps to the edge) plus a rotation (yaw). */
export interface MapMarker {
  u: number;
  v: number;
  rot: number;
  inBounds: boolean;
}

/** A world-locked 128×128 map: its RGBA buffer fills in as `refresh` sees columns.
 *  A pixel's alpha is 0 until first sampled (blank on the map), then 255. */
export class MapSnapshot {
  /** 128×128 RGBA; alpha 0 marks an as-yet-unexplored (blank) pixel. */
  readonly rgba: Uint8ClampedArray;
  /** A pinned world target (treasure-map "X" / structure marker), or null (M18). */
  target: { x: number; z: number } | null = null;

  constructor(readonly id: number, readonly scale: number, readonly cx: number, readonly cz: number) {
    this.rgba = new Uint8ClampedArray(MAP_PX * MAP_PX * 4);
  }

  get originX(): number { return this.cx - mapSpan(this.scale) / 2; }
  get originZ(): number { return this.cz - mapSpan(this.scale) / 2; }

  /** NW-corner world column a pixel samples. */
  pixelCorner(u: number, v: number): { wx: number; wz: number } {
    const bpp = blocksPerPixel(this.scale);
    return { wx: this.originX + u * bpp, wz: this.originZ + v * bpp };
  }

  /** Fractional pixel coords of a world point (may be <0 or ≥128 when off-map). */
  worldToPixel(wx: number, wz: number): { u: number; v: number; inBounds: boolean } {
    const bpp = blocksPerPixel(this.scale);
    const u = (wx - this.originX) / bpp;
    const v = (wz - this.originZ) / bpp;
    return { u, v, inBounds: u >= 0 && u < MAP_PX && v >= 0 && v < MAP_PX };
  }

  private write(u: number, v: number, rgb: [number, number, number] | null): boolean {
    const o = (v * MAP_PX + u) * 4;
    const wasBlank = this.rgba[o + 3] === 0;
    if (rgb) {
      this.rgba[o] = rgb[0]; this.rgba[o + 1] = rgb[1]; this.rgba[o + 2] = rgb[2]; this.rgba[o + 3] = 255;
      return wasBlank;
    }
    return false;
  }

  /** [r,g,b,a] of a pixel (a=0 ⇒ blank). Out-of-range ⇒ transparent black. */
  pixelAt(u: number, v: number): [number, number, number, number] {
    if (u < 0 || u >= MAP_PX || v < 0 || v >= MAP_PX) return [0, 0, 0, 0];
    const o = (v * MAP_PX + u) * 4;
    return [this.rgba[o]!, this.rgba[o + 1]!, this.rgba[o + 2]!, this.rgba[o + 3]!];
  }

  /** Count of pixels sampled so far (alpha 255). */
  mappedCount(): number {
    let n = 0;
    for (let i = 3; i < this.rgba.length; i += 4) if (this.rgba[i] === 255) n++;
    return n;
  }

  /** Sample every pixel (treasure maps / explicit full fill). */
  paint(sampler: MapSampler, palette: MapPalette): void {
    for (let v = 0; v < MAP_PX; v++) for (let u = 0; u < MAP_PX; u++) {
      this.write(u, v, computeCell(sampler, palette, this.scale, this.cx, this.cz, u, v).rgb);
    }
  }

  /** Re-sample pixels whose column lies within `radius` blocks of (px,pz) — the
   *  world-locked "fills in as you explore" update. Returns newly-mapped pixels. */
  refresh(sampler: MapSampler, palette: MapPalette, px: number, pz: number, radius: number): number {
    const r2 = radius * radius;
    let filled = 0;
    for (let v = 0; v < MAP_PX; v++) for (let u = 0; u < MAP_PX; u++) {
      const { wx, wz } = this.pixelCorner(u, v);
      const dx = wx - px, dz = wz - pz;
      if (dx * dx + dz * dz > r2) continue;
      if (this.write(u, v, computeCell(sampler, palette, this.scale, this.cx, this.cz, u, v).rgb)) filled++;
    }
    return filled;
  }

  /** The player pointer: pixel position + yaw rotation, flagged when on-map. */
  marker(wx: number, wz: number, yaw: number): MapMarker {
    const { u, v, inBounds } = this.worldToPixel(wx, wz);
    return { u, v, rot: yaw, inBounds };
  }

  /** A fixed target pin (treasure-map "X" / structure marker). */
  targetMarker(tx: number, tz: number): MapMarker {
    const { u, v, inBounds } = this.worldToPixel(tx, tz);
    return { u, v, rot: 0, inBounds };
  }
}

/** Level-wide store of map snapshots keyed by the `mapId` filled-map stacks carry. */
export class MapStore {
  private readonly maps = new Map<number, MapSnapshot>();
  private next = 0;

  /** Allocate a fresh, empty snapshot centred on the given grid cell. */
  create(scale: number, cx: number, cz: number): MapSnapshot {
    const snap = new MapSnapshot(this.next++, scale, cx, cz);
    this.maps.set(snap.id, snap);
    return snap;
  }

  get(id: number): MapSnapshot | undefined { return this.maps.get(id); }
  get size(): number { return this.maps.size; }
}

// ── Palette (UI-8): per-state base map colour, shared by the map + the minimap ──

/** Parse a `#rrggbb` block mapColor into an RGB triple. */
export function hexRgb(hex: string): [number, number, number] {
  return [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)];
}

/** Material → base map RGB when a block declares no explicit `mapColor`. */
export const MATERIAL_MAP_RGB: Record<string, [number, number, number]> = {
  grass: [95, 160, 58],
  dirt: [138, 96, 67],
  stone: [128, 128, 128],
  sand: [221, 208, 138],
  gravel: [138, 138, 138],
  wood: [122, 90, 48],
  leaves: [58, 122, 40],
  water: [58, 106, 208],
  lava: [224, 96, 26],
  ice: [166, 216, 255],
  snow: [240, 244, 255],
  plant: [74, 154, 48],
  glass: [191, 224, 224],
  wool: [221, 221, 221],
  metal: [192, 192, 192],
};
export const MAP_FALLBACK: [number, number, number] = [102, 102, 102];

/** Build the per-state base-colour palette (air → null). Single source of the
 *  UI-8 map colours for both the crafted map and the minimap radar. */
export function buildStateMapColors(reg: Registries): MapPalette {
  const { states, blocks } = reg;
  const out: Array<[number, number, number] | null> = new Array(states.count);
  for (let s = 0; s < states.count; s++) {
    const def = blocks.byName.get(states.blockName[s]!)!;
    out[s] = def.material === 'air' ? null : def.mapColor ? hexRgb(def.mapColor) : MATERIAL_MAP_RGB[def.material] ?? MAP_FALLBACK;
  }
  return out;
}
